"use client";

import { BriefcaseMedical } from "lucide-react";
import { useState } from "react";

import EditableCard from "@/src/components/cards/editable-card";
import ProviderForm from "@/src/components/cards/provider-form";
import { toast } from "@/src/hooks/use-toast";
import {
  usePractitionerCreateOrUpdateMutation,
  usePractitioners,
} from "@/src/lib/data-fetchers/practitioners-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { convertEmptyStringValuesToNull } from "@/src/lib/utils/object-utils";
import { getPersonName } from "@/src/models/person";
import { Practitioner, PractitionerErrors, PractitionerRole, validatePractitioner } from "@/src/models/practitioner";

interface PractitionerCardEditableProps {
  practitioner: Practitioner;
  onClose: () => void;
  type?: "Referring" | "Receiving" | "Primary Care" | "Ordering";
  // Callback for when user switches to a different practitioner - allows parent to handle the context update
  onEditPractitioner?: (updatedPractitioner: Practitioner) => Promise<void>;
  onEditPractitionerLoading?: boolean;
  createMode?: boolean;
}

// List of fields to validate for this card
const fieldsToValidate: (keyof Practitioner)[] = ["givenName", "middleName", "familyName", "specialty", "role"];

export default function PractitionerCardEditable({
  practitioner,
  onClose,
  type,
  onEditPractitioner,
  onEditPractitionerLoading = false,
  createMode = false,
}: PractitionerCardEditableProps) {
  const [formData, setFormData] = useState<Practitioner>({
    ...practitioner,
  });
  const [errors, setErrors] = useState<PractitionerErrors>({});

  const createOrUpdatePractitionerMutation = usePractitionerCreateOrUpdateMutation();
  const { practitioners, isLoading: isPractitionersLoading } = usePractitioners();

  const handleInputChange = (field: keyof Practitioner, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;

        return rest;
      });
    }
  };

  const handleProviderSelected = (selectedProvider: Practitioner | null) => {
    if (selectedProvider) {
      setErrors({});

      setFormData({
        ...selectedProvider,
        ...(formData.role && !selectedProvider.role ? { role: formData.role } : {}),
      });
    }
  };

  const handleSave = async () => {
    const processedFormData = convertEmptyStringValuesToNull(formData);
    const { isValid, errors } = validatePractitioner(processedFormData, fieldsToValidate);

    setErrors(errors);

    if (!isValid) {
      return;
    }

    // Block creation if npiNumber already exists in existing practitioners with the same role when in create mode
    if (createMode && processedFormData.npiNumber && practitioners) {
      if (!processedFormData.role || processedFormData.role === PractitionerRole.UNKNOWN_PROVIDER) {
        setErrors((prev) => ({
          ...prev,
          role: "Select a role",
        }));

        return;
      }

      const existingPractitionerWithSameNPI = practitioners.find(
        (existingPractitioner) =>
          existingPractitioner.npiNumber === processedFormData.npiNumber &&
          existingPractitioner.role === processedFormData.role
      );

      if (existingPractitionerWithSameNPI) {
        setErrors((prev) => ({
          ...prev,
          npiNumber: "A practitioner with this NPI number already exists with the same role selected.",
        }));

        return;
      }
    }

    try {
      // If onEdit callback is provided, use it to update the parent context's practitioner reference
      if (onEditPractitioner && processedFormData.id !== practitioner.id) {
        await onEditPractitioner(processedFormData);
      }

      // Create or update practitioner entity
      await createOrUpdatePractitionerMutation.mutateAsync(processedFormData);

      onClose?.();

      toast({
        title: createMode ? "Provider created" : "Provider updated",
        description: createMode
          ? `Provider ${getPersonName(processedFormData)} was successfully created.`
          : `Provider ${getPersonName(processedFormData)} was successfully updated.`,
        variant: "default",
      });
    } catch (error) {
      logger.error("Error saving practitioner:", { error });
    }
  };

  return (
    <EditableCard
      title={createMode ? "Create New Provider" : `Edit ${type ? `${type} Provider` : "Provider"}`}
      icon={BriefcaseMedical}
      initialData={practitioner}
      onCancel={onClose}
      onSave={handleSave}
      isSaving={createOrUpdatePractitionerMutation.isPending || onEditPractitionerLoading}
      createMode={createMode}
    >
      <ProviderForm
        provider={formData}
        onChange={(field: keyof Practitioner, value: string) => handleInputChange(field, value)}
        errors={errors}
        practitioners={practitioners}
        loading={isPractitionersLoading}
        onProviderSelected={handleProviderSelected}
        showProviderMatcher={!!onEditPractitioner}
        createMode={createMode}
      />
    </EditableCard>
  );
}
